import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useEventListener, useInterval } from "usehooks-ts"

import {
  selectIsConnected,
  selectShouldPromptBeforeUnload,
  useDappSelector,
} from "#/redux-state"

import { WindowPopupStatus } from "../types"

export const useDropdown = (
  openTrigger: HTMLButtonElement | HTMLDivElement | null,
  close: () => void,
): { dropdownRef: RefObject<HTMLDivElement> } => {
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const closeDropdown = (e: MouseEvent) => {
      if (!dropdownRef.current || !openTrigger) return

      if (
        !dropdownRef.current.contains(e.target as Node) &&
        e.target !== openTrigger
      )
        close()
    }

    window.addEventListener("click", closeDropdown)

    return () => window.removeEventListener("click", closeDropdown)
  }, [dropdownRef, openTrigger, close])

  return { dropdownRef }
}

export const useBeforeUnload = () => {
  const shouldPrompt = useDappSelector(selectShouldPromptBeforeUnload)

  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault()
    }

    if (shouldPrompt) {
      window.addEventListener("beforeunload", handleBeforeUnload)
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [shouldPrompt])
}

export const useSearchParams = (key: string) => {
  const searchParam = useMemo(
    () => new URL(document.location.toString()).searchParams.get(key),
    [key],
  )

  return { searchParam, hasSearchParams: !!searchParam }
}

export const usePopupWindow = (payloadParam: string) => {
  const [popupWindow, setPopupWindow] = useState<Window | null>(null)
  const [popupError, setPopupError] = useState<string | null>(null)

  const [popupStatus, setPopupStatus] = useState<WindowPopupStatus>(
    WindowPopupStatus.IDLE,
  )

  const [popupPayload, setPopupPayload] = useState<string | undefined>()

  const openPopupWindowHandler = useCallback((url: string) => {
    const popup = window.open(url, "_blank", "width=500,height=700")

    if (!popup) {
      setPopupError("Failed to start authorization")
      return
    }

    setPopupError(null)
    setPopupWindow(popup)
    setPopupStatus(WindowPopupStatus.PENDING)
  }, [])

  const closePopup = useCallback(() => {
    if (!popupWindow) return

    popupWindow.close()
    setPopupStatus(WindowPopupStatus.CLOSED)
    setPopupWindow(null)
  }, [popupWindow])

  // Check if popup has been forced closed
  // by directly closing popup window
  useInterval(() => {
    if (popupWindow?.closed) {
      setPopupWindow(null)
      setPopupStatus(WindowPopupStatus.CLOSED)

      // Prevent overriding original error
      if (!popupError) {
        setPopupError("Failed to start authorization")
      }
    }
  }, 500)

  useEventListener("message", (event) => {
    if (!popupWindow || event.origin !== window.location.origin) return

    const error = new URLSearchParams(popupWindow.location.search).get("error")

    const errorDescription = new URLSearchParams(
      popupWindow.location.search,
    ).get("error_description")

    if (error) {
      setPopupError(errorDescription)
      closePopup()
    }

    const code = new URLSearchParams(popupWindow.location.search).get(
      payloadParam,
    )

    if (!code) return

    if (popupStatus !== WindowPopupStatus.CLOSED) {
      popupWindow.opener.postMessage({ code })
      closePopup()
    }

    // We need to retrieve popup message payload
    if (payloadParam in event.data) {
      setPopupError(null)
      setPopupPayload(event.data[payloadParam])
    }
  })

  return {
    openPopupWindow: openPopupWindowHandler,
    popupStatus,
    popupError,
    popupPayload,
  }
}

export const useDraggableContainer = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [dragDistance, setDragDistance] = useState(0)
  const [isScrollable, setIsScrollable] = useState(false)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement

    const isActionItem =
      target.tagName === "BUTTON" ||
      target.tagName === "A" ||
      target.closest("button") ||
      target.closest("a")

    if (isActionItem || !scrollContainerRef.current) return

    setIsDragging(true)
    setStartX(e.pageX)
    setScrollLeft(scrollContainerRef.current.scrollLeft)
    setDragDistance(0)
  }, [])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !scrollContainerRef.current) return

      const moveX = e.pageX - startX

      if (Math.abs(moveX) > 2) {
        setDragDistance(Math.abs(moveX))
      }

      scrollContainerRef.current.scrollLeft = scrollLeft - moveX * 1.5
    },
    [isDragging, scrollLeft, startX],
  )

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(false)

      if (dragDistance > 5) {
        e.stopPropagation()
      }

      setDragDistance(0)
    },
    [dragDistance],
  )

  const handleButtonClickMove = useCallback(
    (direction: "forward" | "backward") => {
      if (!scrollContainerRef.current) return

      scrollContainerRef.current.style.scrollBehavior = "smooth"
      const scrollValue = direction === "forward" ? 300 : -300
      setScrollLeft((prev) => prev + scrollValue)

      scrollContainerRef.current.scroll({
        left: scrollContainerRef.current.scrollLeft + scrollValue,
      })

      scrollContainerRef.current.style.scrollBehavior = "auto"
    },
    [],
  )

  const setIsScrollableHandler = useCallback(() => {
    if (!scrollContainerRef.current) return

    setIsScrollable(
      scrollContainerRef.current.scrollWidth >
        scrollContainerRef.current.clientWidth,
    )
  }, [scrollContainerRef])

  useEventListener("resize", () => setIsScrollableHandler)

  useEffect(() => {
    setIsScrollableHandler()
  }, [setIsScrollableHandler])

  return {
    scrollContainerRef,
    isDragging,
    isScrollable,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleButtonClickMove,
  }
}

export const useResetStateOnDisconnect = <T>(
  setState: Dispatch<SetStateAction<T>>,
  resetValue: T,
) => {
  const isConnected = useDappSelector(selectIsConnected)

  useEffect(() => {
    // Reset state when account is disconnected
    if (!isConnected) {
      setState(resetValue)
    }
  }, [isConnected, resetValue, setState])
}

/**
 * Custom hook that returns a debounced version of the input value.
 *
 * It delays updating the returned value until after the specified `delay` has
 * passed since the last change to `value`. This is useful for performance
 * optimization in situations like API calls or validation that shouldn't run on
 * every keystroke.
 *
 * @param value - The value to debounce.
 * @param delay - The debounce delay in milliseconds.
 * @param onDebounce - Optional callback fired after debounce completes.
 * @returns The debounced value.
 */
export function useDebounce<T>(
  value: T,
  delay: number,
  onDebounce?: (debouncedValue: T) => void,
): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
      if (onDebounce) onDebounce(value)
    }, delay)

    return () => clearTimeout(timer)
  }, [value, delay, onDebounce])

  return debouncedValue
}
